import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  IPromotion,
  IDataGrouped,
  IPromotionResult,
  Result,
} from 'interfaces/SalesInfo';
import { groupBy } from 'utils/constants/functions';

export type PromotionsState = {
  changeCTKM: boolean;
  SAPPromotions: IDataGrouped[];
  vouchers: IPromotionResult['results'];
  manualVouchers: IDataGrouped[];
};

export const usePromotionManager = () => {
  // States
  const [promotionData, setPromotionData] = useState<IPromotion>(
    {} as IPromotion,
  );
  const [promotions, setPromotions] = useState<PromotionsState>({
    changeCTKM: false,
    SAPPromotions: [] as IDataGrouped[],
    vouchers: [] as IPromotionResult['results'],
    manualVouchers: [] as IDataGrouped[],
  });

  // Merged data
  const mergedData = useMemo(
    () => ({
      ...promotionData,
      ...promotions,
    }),
    [promotionData, promotions],
  );

  // Actions
  const updatePromotions = useCallback((newData: PromotionsState) => {
    setPromotions(newData);
  }, []);

  const updatePromotionData = useCallback((newData: IPromotion) => {
    setPromotionData(newData);
  }, []);

  // Auto sync promotions when promotionData changes
  const syncPromotionsFromPromotionData = useCallback(() => {
    if (!promotionData) return;

    const manualVouchers: Result[] = [];
    const vouchers = (promotionData?.PromoHeader?.results ?? []).reduce(
      (acc: Result[], data) => {
        const index = promotionData?.Voucher?.results.findIndex(
          (voucher) => voucher?.SaleDeal === data?.SaleDeal,
        );

        if (index < 0) {
          manualVouchers.push(data);
          return acc;
        }

        acc.push(data);
        return acc;
      },
      [],
    );

    const groupedManualVouchers = groupBy(
      [...(promotionData?.PromoItem?.results ?? []), ...manualVouchers],
      {
        groupBy: 'ConditionType',
        keys: [
          'Batch',
          'ConditionType',
          'ConditionValue',
          'DiscountValue',
          'Item',
          'Key',
          'Material',
          'Note',
          'ProcessPrice',
          'ProcessPriceSAP',
          'SaleDeal',
          'SaleDealName',
          'TextScan',
          'Type',
          'Serial',
        ],
      },
    );

    const promotionPrograms = groupBy(
      promotionData?.OutputDiscount?.results ?? [],
      {
        groupBy: 'ConditionType',
        keys: [
          'ConditionNum',
          'ConditionType',
          'ConditionName',
          'ConditionValue',
          'Type',
        ],
      },
    );

    setPromotions({
      changeCTKM: false,
      SAPPromotions: promotionPrograms,
      vouchers: vouchers,
      manualVouchers: groupedManualVouchers,
    });
  }, [promotionData]);

  useEffect(() => {
    syncPromotionsFromPromotionData();
  }, [syncPromotionsFromPromotionData]);

  // Helpers
  const hasPromotionData = useMemo(() => {
    return (
      promotions.SAPPromotions.length > 0 ||
      promotions.vouchers.length > 0 ||
      promotions.manualVouchers.length > 0
    );
  }, [promotions]);

  const totalVouchers = useMemo(() => {
    return promotions.vouchers.length + promotions.manualVouchers.length;
  }, [promotions]);

  return {
    // States
    promotionData,
    promotions,
    mergedData,
    // Actions
    updatePromotions,
    updatePromotionData,
    // Helpers
    hasPromotionData,
    totalVouchers,
  };
};
